import { NoMarginParagraph } from '@/components/shared/components/LabelValuePair/LabelValuePair.StyledComponents';
import styled from '@emotion/styled';
import { Button, Card, H2, H3, Link, rem } from '@triumph/design-system';
import { ProgressSpinner } from 'primereact/progressspinner';

export const ApplicantTab = styled.span`
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${rem(4)};
`;

export const TabBadgeSpinner = styled(ProgressSpinner)`
    width: ${rem(16)};
    height: ${rem(16)};
    margin-left: ${rem(4)};
`;

export const Wrapper = styled.div`
    display: flex;
    gap: ${rem(24)};
    height: 100%;
    overflow-x: auto;
`;

export const DDDContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: ${rem(8)};
    margin-top: ${rem(8)};
`;

export const DDDTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

export const DDDTableValue = styled.td`
    text-align: left;
    vertical-align: top;
    padding: ${rem(6)} ${rem(8)};
    white-space: pre-wrap;
    word-break: break-word;
`;

export const NoMarginH2 = styled(H2)`
    margin: 0;
`;

export const DDDText = styled.pre`
    text-wrap: wrap;
    margin: 0;
    font-size: ${rem(12)};
    color: ${({ theme }) => theme.colors.greyscale.gray50};
    background: ${({ theme }) => theme.colors.greyscale.white};
    overflow-x: auto;
    border: ${rem(3)} solid ${({ theme }) => theme.colors.greyscale.gray15};
    padding: ${rem(8)};
    border-radius: ${rem(8)};
    word-break: break-all;
`;

export const CardStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
`;

export const DDDFieldWrapper = styled.div`
    background-color: white;
    padding: ${rem(20)};
    display: flex;
    flex-wrap: wrap;
    gap: ${rem(24)};

    & div {
        flex: 1 1 calc(50% - 24px);
    }
`;

export const DDDTitle = styled(H2)`
    margin: 0;
`;

export const CARD_MAX_WIDTH = rem(1100);

export const DDDCard = styled.div`
    max-width: ${CARD_MAX_WIDTH};
    background: #ffffff;
    border-radius: ${rem(10)};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;
`;

export const DDDCardHeader = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: ${rem(4)} ${rem(16)};
    padding: ${rem(12)} ${rem(20)};
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
`;

// A collapsed card has no body under the header, so its divider would sit on the card's bottom edge.
export const CollapsibleCardHeader = styled(DDDCardHeader)<{ $collapsed: boolean }>`
    border-bottom: ${({ $collapsed }) => ($collapsed ? 'none' : '1px solid #e2e8f0')};
`;

export const CardToggle = styled.button`
    display: flex;
    align-items: center;
    gap: ${rem(8)};
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
`;

export const CardChevron = styled.span`
    width: ${rem(12)};

    @media print {
        display: none;
    }
`;

export const DDDCardTitle = styled(H2)`
    margin: 0;
    color: #334155;
    font-size: ${rem(17)};
    font-weight: 600;
`;

export const DDDCardPulled = styled.span`
    color: #64748b;
    font-size: ${rem(13)};
`;

export const DDDCardSection = styled.section`
    border-top: 1px solid #e2e8f0;
`;

export const DDDCardSectionTitle = styled(H3)`
    margin: 0;
    padding: ${rem(10)} ${rem(20)};
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
    font-size: ${rem(15)};
    font-weight: 600;
`;

export const DDDRowList = styled.div`
    padding: ${rem(6)} 0;
`;

export const DDDRow = styled.div`
    display: grid;
    grid-template-columns: ${rem(250)} minmax(0, 1fr);
    gap: ${rem(16)};
    padding: ${rem(6)} ${rem(20)};
    font-size: ${rem(13.5)};
    line-height: 1.45;

    &:nth-of-type(even) {
        background-color: ${({ theme }) => theme.colors.greyscale.gray2_5};
    }
`;

// Beats the stripe on even rows, so a changed row reads the same wherever it falls.
export const ChangedDDDRow = styled(DDDRow)`
    &,
    &:nth-of-type(even) {
        background-color: #fef3c7;
        box-shadow: inset ${rem(3)} 0 0 #f59e0b;
    }
`;

// One wrapper per group, so the stripes restart under each heading.
export const DDDRowGroup = styled.div``;

export const DDDRowLabel = styled.div`
    color: #64748b;
    font-weight: 500;
    word-break: break-word;
`;

export const DDDRowValue = styled.div`
    color: #1e293b;
    white-space: pre-wrap;
    word-break: break-word;

    & a {
        color: #3b82f6;
    }
`;

export const DDDGroupHeading = styled.div`
    margin: ${rem(14)} ${rem(20)} ${rem(4)};
    padding-top: ${rem(10)};
    border-top: 1px solid #e2e8f0;
    color: #1e293b;
    font-size: ${rem(13.5)};
    font-weight: 600;

    &:first-child {
        border-top: none;
        margin-top: ${rem(4)};
        padding-top: 0;
    }
`;

export const KyLink = styled(Link)`
    margin: 0;
    cursor: pointer;
    text-decoration: underline;
`;

export const SubHeaderWrapper = styled.div`
    display: flex;
    gap: ${rem(24)};
    justify-content: end;
`;

export const OwnerTitle = styled.h3`
    margin: 0;
    color: ${({ theme }) => theme.colors.greyscale.gray50};
    text-transform: uppercase;
    font-size: ${rem(14)};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    font-family: ${({ theme }) => theme.typography.fontFamilies.secondary};
    letter-spacing: ${rem(0.65)};
    line-height: ${rem(32)};
`;

export const DDDFileThumbnailLink = styled.a`
    display: inline-block;
    align-self: flex-start;
    width: fit-content;
    margin-top: ${rem(4)};
    line-height: 0;
`;

export const DDDFilePreviewLoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${rem(120)};
    height: ${rem(80)};
`;

export const DDDFilePreviewErrorWrapper = styled.span`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${rem(4)};
    width: ${rem(120)};
    height: ${rem(80)};
    padding: ${rem(4)};
    color: ${({ theme }) => theme.colors.semantic.error50};
    font-size: ${rem(11)};
    line-height: ${rem(14)};
    text-align: center;
`;

export const DDDFilePreviewRetryButton = styled.button`
    padding: 0;
    border: none;
    background: none;
    color: ${({ theme }) => theme.colors.primary.actionDefault};
    font: inherit;
    text-decoration: underline;
    cursor: pointer;

    &:focus-visible {
        outline: ${rem(2)} solid ${({ theme }) => theme.colors.primary.actionDefault};
        outline-offset: ${rem(2)};
    }
`;

export const DDDFileThumbnailImage = styled.img`
    display: block;
    width: ${rem(120)};
    height: ${rem(80)};
    object-fit: cover;
`;

export const DDDFileFallbackLink = styled.a`
    color: #0000ee;
    text-decoration: underline;
    cursor: pointer;
`;

export const FileButton = styled(Button)`
    justify-content: start;
    width: fit-content;

    > div {
        padding: ${rem(4)};
        font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
        font-size: ${({ theme }) => theme.typography.fontSizes.label.large};
        text-align: left;
        text-decoration-color: transparent;
    }

    > div:hover {
        text-decoration-color: transparent;
    }
`;

export const Container = styled(Card)`
    min-height: auto;
`;

export const ContentWrapper = styled.div`
    padding: ${rem(28)};

    > div {
        position: static;
    }
`;

// Every tab starts at the same inset. Each tab's root keeps its own outer spacing for
// the print view, so the panel zeroes it here instead.
export const TabPanel = styled.div`
    padding: ${rem(16)} ${rem(16)} ${rem(16)} 0;

    && > * {
        margin: 0;
        padding: 0;
    }
`;

export const InfoTitle = styled(NoMarginParagraph)`
    font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
`;

export const StatusWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.greyscale.gray2_5};
    border: ${rem(1)} solid ${({ theme }) => theme.colors.greyscale.gray15};
    margin: ${rem(32)};
    padding: 0 ${rem(12)};
`;

// One column per action button so a button's position is fixed regardless of row content.
// align-items: stretch keeps every cell in a row the same height, so a row's divider stays
// one continuous line even if a cell's content grows taller than its neighbors.
export const StatusGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto auto auto auto auto auto;
    align-items: stretch;
`;

// display: contents merges each row's cells into the shared StatusGrid column tracks.
export const StatusWrapperRow = styled.div`
    display: contents;
`;

export const StatusGridCell = styled.div<{ isLast?: boolean; showTopBorder?: boolean }>`
    display: flex;
    align-items: center;
    padding: 0 ${rem(12)};
    min-height: ${rem(52)};
    border-top: ${({ showTopBorder, theme }) =>
        showTopBorder ? `${rem(1)} solid ${theme.colors.greyscale.gray15}` : 'none'};
`;

export const RecentlyCompletedRequestBanner = styled.div`
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${rem(8)};
    padding: ${rem(8)} ${rem(16)};
    background: #f0fdf4;
    border-top: ${rem(1)} solid #bbf7d0;
    font-size: ${rem(13)};
    color: #166534;
`;
